<script>
    import { getContext } from "svelte";
    import { path } from 'd3-path';
    import { select } from "d3-selection";
    import { derived, writable } from "svelte/store";

    // --- Properties
    export let dataKey;
    export let transactionKey;
    export let weekday;
    export let dayNr;

    let width;
    let height;
    let ccAxisG;


    // --- Contexts
    const { selectedCCs, selectedLYs, matchTransactions } = getContext(dataKey);
    const { timeScale, ccScale, lyScale, ccAxis } = getContext(transactionKey);
    

    // --- Get transactions from database
    const ccTransactions = writable({});
    const lyTransactions = writable([]);
    $: if ($selectedCCs.length > 0 && $selectedLYs.length > 0) {
        matchTransactions(dayNr, $selectedCCs, $selectedLYs).then(res => {
            const {ccValues, lyValues} = res;
            lyTransactions.update(() => lyValues); 
            ccTransactions.update(() => ccValues.reduce((map, cc) => {
                if (cc.ccnum in map) {
                    map[cc.ccnum].push(cc);
                } else {
                    map[cc.ccnum] = [cc];
                }
                return map;
            }, {}));
        });    
    } else {
        $ccTransactions = {};
        $lyTransactions = [];
    }

    // LY transaction used doubly
    const doubleMatches = derived(ccTransactions, ($ccTransactions) => {
        const ccs = Object.keys($ccTransactions);
        const doubles = [];
        for (let i = 0; i < ccs.length; i++) {
            const i_ts = $ccTransactions[ccs[i]];
            for (let j = i+1; j < ccs.length; j++) {
                const j_ts = $ccTransactions[ccs[j]];
                for (const i_t of i_ts) {
                    for (const j_t of j_ts) {
                        if (i_t.discount_price === j_t.discount_price &&
                            i_t.location === j_t.location) {
                            // check overlap in lynums
                            const overlap = i_t.lynums.filter(lynum => j_t.lynums.includes(lynum));
                            if (overlap.length > 0) {
                                doubles.push([i_t, j_t]);
                            }
                        }
                    }
                }
            }
        }
        return doubles;
    });

    // --- Draw axes
    $: updateAxis($selectedCCs);

    function updateAxis(_) {
        if (ccAxisG) {
            select(ccAxisG)
                .call(ccAxis)
                .call(g => g.select('.domain').remove());
        }
    }


    // --- Draw transactions
    function transactionColor(lynums) {
        if (lynums.length == 0) {
            return 'black';
        }
        if (lynums.length > 1) {
            return 'red';
        }
        return lyScale(lynums[0]);
    }

    function dMatchPath(dMatch) {
        const p = path();
        const h = ((height-25) / $selectedCCs.length - 4) / 2;
        const x1 = timeScale(dMatch[0].time_of_day);
        const x2 = timeScale(dMatch[1].time_of_day);
        const y1 = ccScale(dMatch[0].ccnum) + h;
        const y2 = ccScale(dMatch[1].ccnum) + h;

        p.moveTo(x1, y1);
        p.bezierCurveTo(x2, y1, x1, y2, x2, y2);
        return p.toString();
    }
    
    // --- Tooltip
    let showTooltip = false;
    let tooltipTransaction = null;
    let tooltipCoords = [0, 0];

    function startTooltip(event, transaction) {
        tooltipTransaction = transaction;
        moveTooltip(event);
        showTooltip = true;
    }

    function stopTooltip(event) {
        showTooltip = false;
        tooltipTransaction = null;
        tooltipCoords = [0, 0];
    }

    function moveTooltip(event) {
        const rect = event.currentTarget.getBoundingClientRect()
        tooltipCoords = [event.clientX - rect.x + 5, event.clientY - rect.y + 5];
    }

</script>


<div class='day col' bind:clientWidth={width} bind:clientHeight={height}>
    <span class='dayname'>{weekday} January {dayNr}</span>
    <svg {width} {height} on:mousemove={moveTooltip}>
        <g class='missing-transactions'>
            {#each $lyTransactions as t, i}
                <rect   x={3+i*10}
                        y=15
                        fill={transactionColor(t.lynums)}
                        width=5
                        height=5
                        stroke-width=0
                        on:mouseover={(e) => startTooltip(e, t)}
                        on:mouseleave={stopTooltip} />
            {/each}
        </g>
        <g transform='translate(0, 20)'>
            <g class='ccaxis' bind:this={ccAxisG} transform='translate(35, 0)'></g>
            {#each $doubleMatches as d}
                <!-- svelte-ignore component-name-lowercase -->
                <path class='path' d = {dMatchPath(d)} stroke='currentColor' 
                      fill='transparent' />
            {/each}
            {#each Object.keys($ccTransactions) as cc}
                {#each $ccTransactions[cc] as t}
                    <rect x={timeScale(t.time_of_day)}
                          y={ccScale(cc)}
                          stroke={transactionColor(t.lynums)}
                          width=1
                          height={(height-25) / $selectedCCs.length - 4}
                          stroke-width=2
                          fill='transparant'
                          on:mouseover={(e) => startTooltip(e, t)}
                          on:mouseleave={stopTooltip} />
                {/each}
            {/each}
        </g>
    </svg>
    {#if showTooltip}
        <div class="tooltip" style="left:{tooltipCoords[0]}px;top:{tooltipCoords[1]}px">
            <ul>
                <li><b>Time</b>: {tooltipTransaction.display_time}</li>
                <li><b>Location</b>: {tooltipTransaction.location}</li>
                <li><b>Price</b>: {tooltipTransaction.price}</li>
                <li><b>Discount price</b>: {tooltipTransaction.discount_price}</li>
                <li><b>CC</b>: {tooltipTransaction.ccnum}</li>
                <li><b>Loyalty</b>: {tooltipTransaction.lynums}</li>
            </ul>
        </div>
    {/if}
</div>


<style>
.day {
    height: calc((100% - 15px) / 7);
    vertical-align: center;
    flex-grow: 0;
    flex-shrink: 0;
}
.dayname {
    font-size: 12px;
    position: absolute;
    top: 0px;
    left: 2px;
}
svg {
    position: absolute;
    left: 0;
}
.tooltip {
		border: 1px solid #ddd;
		box-shadow: 1px 1px 1px #ddd;
		background: white;
		border-radius: 4px;
		position: relative;
        font-size: 12px;
        width: 220px;
        z-index: 1;
	}
</style>